import React from 'react';
import { motion } from 'framer-motion';

interface HeatMapChartProps {
  data: {
    hours: string[];
    days: string[];
    values: number[][];
  };
}

export function HeatMapChart({ data }: HeatMapChartProps) {
  const maxValue = Math.max(...data.values.flat());

  const getColor = (value: number) => {
    if (value === 0) return 'bg-gray-100';
    const intensity = value / maxValue;
    if (intensity > 0.8) return 'bg-indigo-600';
    if (intensity > 0.6) return 'bg-indigo-500';
    if (intensity > 0.4) return 'bg-indigo-400';
    if (intensity > 0.2) return 'bg-indigo-300';
    return 'bg-indigo-200';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">מפת חום - שעות פופולריות</h3>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
          <span>החודש הנבחר</span>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2 sm:mx-0">
        <div className="inline-block min-w-full px-2 sm:px-0">
          <div className="flex gap-0.5 sm:gap-1 mb-2 pr-10 sm:pr-16">
            {data.hours.map((hour) => (
              <div key={hour} className="w-8 sm:w-10 text-center text-xs text-gray-500 font-medium">
                {hour}
              </div>
            ))}
          </div>

          <div className="space-y-0.5 sm:space-y-1">
            {data.days.map((day, dayIndex) => (
              <div key={day} className="flex items-center gap-0.5 sm:gap-1">
                <div className="w-8 sm:w-14 text-xs sm:text-sm text-gray-700 font-medium text-right">
                  {day}
                </div>
                <div className="flex gap-0.5 sm:gap-1">
                  {data.hours.map((hour, hourIndex) => {
                    const value = data.values[dayIndex]?.[hourIndex] || 0;
                    return (
                      <motion.div
                        key={`${day}-${hour}`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: (dayIndex * data.hours.length + hourIndex) * 0.01 }}
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${getColor(value)} hover:scale-110 transition-all duration-200 cursor-pointer flex items-center justify-center group relative`}
                        title={`${day} ${hour}:00 - ${value} תורים`}
                      >
                        {value > 0 && (
                          <span className="text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity">
                            {value}
                          </span>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-4">
        <span className="text-sm text-gray-500">פחות עמוס</span>
        <div className="flex gap-1">
          <div className="w-4 h-4 rounded bg-gray-100"></div>
          <div className="w-4 h-4 rounded bg-indigo-200"></div>
          <div className="w-4 h-4 rounded bg-indigo-300"></div>
          <div className="w-4 h-4 rounded bg-indigo-400"></div>
          <div className="w-4 h-4 rounded bg-indigo-500"></div>
          <div className="w-4 h-4 rounded bg-indigo-600"></div>
        </div>
        <span className="text-sm text-gray-500">יותר עמוס</span>
      </div>
    </motion.div>
  );
}
